import React, { useState, useEffect, useRef } from 'react';
import { X, Loader, Check } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './NewsletterPopup.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISSED_KEY = 'kostin_newsletter_popup_dismissed';
const SUBSCRIBED_KEY = 'kostin_newsletter_subscribed';
const EXCLUDED_PATHS = ['/checkout', '/admin', '/unsubscribe', '/auth'];

export const NewsletterPopup = () => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [discountCode, setDiscountCode] = useState('');
  const shownRef = useRef(false);
  const eligibleRef = useRef(true);

  // Logged-in users who are already active marketing subscribers should never see this
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/marketing/account/status`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.subscribed) {
          eligibleRef.current = false;
          localStorage.setItem(SUBSCRIBED_KEY, 'true');
          setVisible(false);
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const alreadyDismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
    const alreadySubscribed = localStorage.getItem(SUBSCRIBED_KEY) === 'true';
    const excluded = EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p));
    if (alreadyDismissed || alreadySubscribed || excluded) return;

    const show = () => {
      if (shownRef.current || !eligibleRef.current) return;
      shownRef.current = true;
      setVisible(true);
    };

    const timer = setTimeout(show, 10000);

    const handleScroll = () => {
      if (window.scrollY > document.body.scrollHeight * 0.4) {
        show();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === 'loading') return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/marketing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'popup', language: lang }),
      });
      if (res.ok) {
        const data = await res.json();
        setDiscountCode(data.discount_code || '');
        setStatus('success');
        localStorage.setItem(SUBSCRIBED_KEY, 'true');
        localStorage.setItem(DISMISSED_KEY, 'true');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Newsletter popup subscribe failed:', err);
      setStatus('error');
    }
  };

  if (!visible) return null;

  return (
    <div className="newsletter-popup-overlay" onClick={handleClose} data-testid="newsletter-popup-overlay">
      <div className="newsletter-popup-card" onClick={(e) => e.stopPropagation()} data-testid="newsletter-popup">
        <button className="newsletter-popup-close" onClick={handleClose} aria-label="Close" data-testid="newsletter-popup-close-btn">
          <X size={18} />
        </button>

        {status === 'success' ? (
          <div className="newsletter-popup-success" data-testid="newsletter-popup-success">
            <div className="newsletter-popup-success-icon"><Check size={28} /></div>
            <h3 className="newsletter-popup-headline">{lang === 'bg' ? 'Добре дошли в KOSTIN' : 'Welcome to KOSTIN'}</h3>
            <p className="newsletter-popup-text">
              {lang === 'bg' ? 'Вашият код за -5% от първата поръчка:' : 'Your code for -5% off your first order:'}
            </p>
            <div className="newsletter-popup-code" data-testid="newsletter-popup-discount-code">{discountCode}</div>
            <button className="newsletter-popup-cta" onClick={handleClose} data-testid="newsletter-popup-done-btn">
              {lang === 'bg' ? 'РАЗГЛЕДАЙ КОЛЕКЦИЯТА' : 'EXPLORE THE COLLECTION'}
            </button>
          </div>
        ) : (
          <>
            <h3 className="newsletter-popup-headline">
              {lang === 'bg' ? 'Нещо специално за Вас.' : 'A little something for you.'}
            </h3>
            <p className="newsletter-popup-text">
              {lang === 'bg'
                ? 'Присъединете се към KOSTIN и получете -5% от първата поръчка, плюс ранен достъп до нови аромати и подбрани оферти.'
                : 'Join KOSTIN and receive 5% off your first order, plus early access to new fragrances and selected offers.'}
            </p>
            <form onSubmit={handleSubmit} className="newsletter-popup-form">
              <input
                type="email"
                required
                placeholder={lang === 'bg' ? 'Вашият имейл адрес' : 'Your email address'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="newsletter-popup-input"
                data-testid="newsletter-popup-email-input"
              />
              <button type="submit" className="newsletter-popup-cta" disabled={status === 'loading'} data-testid="newsletter-popup-submit-btn">
                {status === 'loading' ? <Loader size={16} className="spinning" /> : (lang === 'bg' ? 'ВЗЕМИ -5%' : 'GET 5% OFF')}
              </button>
            </form>
            {status === 'error' && (
              <p className="newsletter-popup-error" data-testid="newsletter-popup-error">
                {lang === 'bg' ? 'Нещо се обърка. Опитайте отново.' : 'Something went wrong. Please try again.'}
              </p>
            )}
            <p className="newsletter-popup-consent">
              {lang === 'bg'
                ? 'С абонирането се съгласявате да получавате маркетингови имейли от KOSTIN. Може да се отпишете по всяко време.'
                : 'By subscribing you agree to receive marketing emails from KOSTIN. You can unsubscribe at any time.'}
              {' '}
              <Link to="/privacy" onClick={handleClose}>{lang === 'bg' ? 'Политика за поверителност' : 'Privacy Policy'}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterPopup;
